import React, { useEffect, useMemo, useState } from 'react';
import {
    Checkmark16Regular,
    ChevronDown16Regular,
    ChevronRight16Regular,
    Dismiss16Regular,
    LockClosed16Regular,
    People16Regular,
    Person16Regular,
    Search16Regular,
} from '@fluentui/react-icons';
import styles from './index.module.css';
import { formatCardDate, getNoteLastUpdated, getNotePreview, getSectionVisibility } from '../journalUtils';
import { getAuthorInitials } from './analysisUtils';

const Checkbox = ({ checked, indeterminate, onClick }) => {
    const className = [
        styles.checkbox,
        checked ? styles.checkboxChecked : '',
        !checked && indeterminate ? styles.checkboxIndeterminate : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <span
            className={className}
            role="checkbox"
            aria-checked={indeterminate ? 'mixed' : checked}
            onClick={e => {
                e.stopPropagation();
                onClick?.(e);
            }}>
            {checked ? <Checkmark16Regular /> : null}
            {!checked && indeterminate ? <span className={styles.checkboxDash} /> : null}
        </span>
    );
};

const ContextModal = ({ open, sections = [], projectUsersAll = [], initialSelectedIds = [], onClose, onConfirm }) => {
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(() => new Set());
    const [selectedIds, setSelectedIds] = useState(() => new Set(initialSelectedIds));

    useEffect(() => {
        if (!open) return;
        setSelectedIds(new Set(initialSelectedIds.map(Number).filter(Boolean)));
        setSearch('');
        const firstWithNotes = (sections || []).find(s => (s.notes || []).length > 0);
        setExpanded(firstWithNotes ? new Set([firstWithNotes.id]) : new Set());
    }, [open, initialSelectedIds, sections]);

    const getMemberFullName = id =>
        projectUsersAll?.find(item => item.value.user_id === id)?.value?.full_name || '';

    const filteredSections = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (sections || [])
            .map(section => {
                const notes = section.notes || [];
                if (!q) return { ...section, notes };
                const sectionMatch = (section.name || '').toLowerCase().includes(q);
                const matchedNotes = notes.filter(note => {
                    const { title } = getNotePreview(note.notes);
                    return (title || '').toLowerCase().includes(q);
                });
                if (sectionMatch) return { ...section, notes };
                if (matchedNotes.length) return { ...section, notes: matchedNotes };
                return null;
            })
            .filter(Boolean);
    }, [sections, search]);

    const allNoteIds = useMemo(
        () => (sections || []).flatMap(s => (s.notes || []).map(n => Number(n.id))).filter(Boolean),
        [sections],
    );

    const selectedCount = selectedIds.size;
    const selectedSectionCount = useMemo(() => {
        let count = 0;
        (sections || []).forEach(section => {
            const noteIds = (section.notes || []).map(n => Number(n.id));
            if (noteIds.some(id => selectedIds.has(id))) count += 1;
        });
        return count;
    }, [sections, selectedIds]);

    const allSelected = allNoteIds.length > 0 && allNoteIds.every(id => selectedIds.has(id));

    const toggleExpand = sectionId => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    };

    const expandAll = () => setExpanded(new Set((filteredSections || []).map(s => s.id)));
    const collapseAll = () => setExpanded(new Set());

    const toggleNote = noteId => {
        const id = Number(noteId);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSection = section => {
        const noteIds = (section.notes || []).map(n => Number(n.id));
        if (!noteIds.length) return;
        const allChecked = noteIds.every(id => selectedIds.has(id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            noteIds.forEach(id => {
                if (allChecked) next.delete(id);
                else next.add(id);
            });
            return next;
        });
    };

    const toggleAllJournal = () => {
        if (allSelected) {
            setSelectedIds(new Set());
            return;
        }
        setSelectedIds(new Set(allNoteIds.map(Number)));
    };

    const privacyIcon = label => {
        if (label === 'Private') return <LockClosed16Regular />;
        if (label?.startsWith('Shared with project')) return <People16Regular />;
        return <Person16Regular />;
    };

    if (!open) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose} role="presentation">
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="journal-context-title"
                onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2 id="journal-context-title">Choose Journal context</h2>
                        <p>Select the Sections and Entries that AI should review.</p>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                        <Dismiss16Regular />
                    </button>
                </div>

                <div className={styles.modalToolbar}>
                    <div className={styles.modalSearch}>
                        <Search16Regular color="#989bad" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search Sections and Entries"
                        />
                    </div>
                    <div className={styles.modalActions}>
                        <button type="button" className={styles.linkBtn} onClick={expandAll}>
                            Expand all
                        </button>
                        <button type="button" className={`${styles.linkBtn} ${styles.linkBtnMuted}`} onClick={collapseAll}>
                            Collapse all
                        </button>
                        <span className={styles.dividerV} />
                        <label className={styles.allJournal}>
                            <Checkbox checked={allSelected} onClick={toggleAllJournal} />
                            All Journal content
                        </label>
                    </div>
                </div>

                <div className={styles.tree}>
                    {filteredSections.map(section => {
                        const notes = section.notes || [];
                        const noteIds = notes.map(n => Number(n.id));
                        const checkedCount = noteIds.filter(id => selectedIds.has(id)).length;
                        const allChecked = noteIds.length > 0 && checkedCount === noteIds.length;
                        const indeterminate = checkedCount > 0 && !allChecked;
                        const isExpanded = expanded.has(section.id);
                        const visibility = getSectionVisibility(section);

                        return (
                            <React.Fragment key={section.id}>
                                <div
                                    className={`${styles.sectionRow} ${allChecked || indeterminate ? styles.sectionRowSelected : ''}`}>
                                    <button
                                        type="button"
                                        className={styles.chevronBtn}
                                        onClick={() => toggleExpand(section.id)}
                                        aria-label={isExpanded ? 'Collapse section' : 'Expand section'}>
                                        {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                                    </button>
                                    <Checkbox
                                        checked={allChecked}
                                        indeterminate={indeterminate}
                                        onClick={() => toggleSection(section)}
                                    />
                                    <div className={styles.rowTitle} onClick={() => toggleExpand(section.id)} role="presentation">
                                        <span className={styles.sectionName}>{section.name}</span>
                                        <span className={styles.entryCount}>
                                            {notes.length} {notes.length === 1 ? 'entry' : 'entries'}
                                        </span>
                                    </div>
                                    <span className={styles.privacy}>
                                        {privacyIcon(visibility)}
                                        {visibility}
                                    </span>
                                </div>
                                {isExpanded
                                    ? notes.map(note => {
                                          const { title } = getNotePreview(note.notes);
                                          const fullName = getMemberFullName(note.created_by);
                                          const initials = getAuthorInitials(fullName);
                                          const dateLabel = formatCardDate(getNoteLastUpdated(note));
                                          const checked = selectedIds.has(Number(note.id));
                                          return (
                                              <button
                                                  type="button"
                                                  key={note.id}
                                                  className={styles.entryRow}
                                                  onClick={() => toggleNote(note.id)}>
                                                  <Checkbox checked={checked} onClick={() => toggleNote(note.id)} />
                                                  <span className={styles.entryTitle}>{title || 'Untitled'}</span>
                                                  <span className={styles.entryMeta}>
                                                      {initials}
                                                      {dateLabel ? ` · ${dateLabel}` : ''}
                                                  </span>
                                              </button>
                                          );
                                      })
                                    : null}
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className={styles.summaryBar}>
                    <span>
                        {selectedCount} {selectedCount === 1 ? 'entry' : 'entries'} selected across{' '}
                        <strong>
                            {selectedSectionCount} {selectedSectionCount === 1 ? 'section' : 'sections'}
                        </strong>
                    </span>
                    <button type="button" className={`${styles.linkBtn} ${styles.linkBtnMuted}`} onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </button>
                </div>

                <div className={styles.modalFooter}>
                    <span className={styles.footerCount}>
                        {selectedCount} {selectedCount === 1 ? 'entry' : 'entries'} selected
                    </span>
                    <div className={styles.footerActions}>
                        <button type="button" className={styles.cancelBtn} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.confirmBtn}
                            disabled={selectedCount === 0}
                            onClick={() => onConfirm?.(Array.from(selectedIds))}>
                            Use selected context
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContextModal;
